
import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../common/Button';
import { useStore } from '../../../store/useStore';
import { ItemIcon } from '../../common/ItemIcon';

interface DynamiteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onThrow: (score: number) => void | object | null;
    rockId: string | null;
}

export const DynamiteModal = ({ isOpen, onClose, onThrow, rockId }: DynamiteModalProps) => {
    const [position, setPosition] = useState(0);

    // Phases: AIMING -> EXPLODING -> RESULTS
    const [phase, setPhase] = useState<'AIMING' | 'EXPLODING' | 'RESULTS'>('AIMING');
    const [rewards, setRewards] = useState<Partial<Record<string, number>> | null>(null);
    const [throwQuality, setThrowQuality] = useState<string>('');

    // Animation Ref
    const requestRef = useRef<number>();
    const directionRef = useRef(1);

    const animate = () => {
        setPosition(prev => {
            let next = prev + (directionRef.current * 1.5); // Slightly slower for more "weight"
            if (next >= 100) {
                next = 100;
                directionRef.current = -1;
            } else if (next <= 0) {
                next = 0;
                directionRef.current = 1;
            }
            return next;
        });

        requestRef.current = requestAnimationFrame(animate);
    };

    const reset = () => {
        setPosition(0);
        directionRef.current = 1;
        setPhase('AIMING');
        setRewards(null);
        setThrowQuality('');
        requestRef.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        if (isOpen) {
            setPosition(0);
            directionRef.current = 1;
            setPhase('AIMING');
            setRewards(null);
            setThrowQuality('');
        } else {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        }
    }, [isOpen]);

    useEffect(() => {
        if (phase === 'AIMING' && isOpen) {
            requestRef.current = requestAnimationFrame(animate);
        }
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        }
    }, [phase, isOpen]);

    const handleDetonate = () => {
        if (phase !== 'AIMING') return;

        if (requestRef.current) cancelAnimationFrame(requestRef.current);

        // Calculate Score
        const dist = Math.abs(50 - position);
        let score = 0;
        let quality = 'MISS';

        if (dist < 5) { score = 100; quality = 'PERFECT'; }
        else if (dist < 15) { score = 75; quality = 'GREAT'; }
        else if (dist < 30) { score = 50; quality = 'GOOD'; }
        else { score = 25; quality = 'WEAK'; }

        setThrowQuality(quality);

        // Trigger Explosion Phase
        setPhase('EXPLODING');

        const result = onThrow(score);
        if (result && typeof result === 'object') {
            setRewards(result as any);
        }

        // Transition to results faster, but allow explosion to animate
        setTimeout(() => {
            setPhase('RESULTS');
        }, 800);
    };

    if (!isOpen) return null;

    const getZoneColor = () => {
        const dist = Math.abs(50 - position);
        if (dist < 5) return '#22c55e'; // Green
        if (dist < 15) return '#eab308'; // Yellow
        return '#ef4444'; // Red
    };

    const getQualityColor = () => {
        if (throwQuality === 'PERFECT') return '#22c55e';
        if (throwQuality === 'GREAT') return '#eab308';
        return '#ef4444';
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)', // Darker backdrop
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
            transition: 'all 0.5s ease'
        }} onClick={() => { if (phase === 'RESULTS') onClose(); }}>

            <div
                className={`dynamite-modal-container ${phase === 'EXPLODING' ? 'shaking' : ''}`}
                style={{
                    width: '500px',
                    minHeight: '400px',
                    background: 'linear-gradient(145deg, #1e293b, #0f172a)',
                    border: '1px solid #334155',
                    borderRadius: '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: phase === 'EXPLODING'
                        ? '0 0 100px rgba(239, 68, 68, 0.6)'
                        : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: phase === 'RESULTS' ? 'scale(1.05)' : 'scale(1)'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Background Grid Pattern */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    opacity: 0.1,
                    pointerEvents: 'none'
                }} />

                {/* Main Content Area */}
                <div style={{
                    position: 'relative',
                    zIndex: 10,
                    padding: '40px 40px 20px 40px', // Reduced bottom padding slightly
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>

                    {/* Header - Fades out slightly during explosion */}
                    <div style={{
                        opacity: phase === 'EXPLODING' ? 0.5 : 1,
                        transition: 'opacity 0.3s',
                        marginBottom: '40px',
                        textAlign: 'center'
                    }}>
                        <h2 style={{
                            fontSize: '2rem',
                            fontWeight: 900,
                            letterSpacing: '-0.02em',
                            margin: 0,
                            background: 'linear-gradient(to right, #ffffff, #94a3b8)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase'
                        }}>
                            Detonate Charge
                        </h2>
                        {phase === 'AIMING' && (
                            <p style={{ color: '#64748b', marginTop: '8px', fontSize: '0.9rem', fontWeight: 500 }}>
                                Precision matters. Maximise yield.
                            </p>
                        )}
                    </div>

                    {/* The Gauge - Transitions to broken visualization */}
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '60px',
                        marginBottom: '40px',
                        transition: 'all 0.5s ease',
                        opacity: phase === 'RESULTS' ? 0.3 : 1,
                        filter: phase === 'RESULTS' ? 'grayscale(100%)' : 'none',
                        transform: phase === 'RESULTS' ? 'scale(0.9)' : 'scale(1)'
                    }}>
                        {/* Bar Background */}
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: '#0f172a',
                            borderRadius: '30px',
                            border: '2px solid #334155',
                            boxShadow: 'inset 0 4px 6px rgba(0,0,0,0.3)',
                            overflow: 'hidden'
                        }}>
                            {/* Sweet Spot */}
                            <div style={{
                                position: 'absolute',
                                left: '45%',
                                width: '10%',
                                height: '100%',
                                background: 'linear-gradient(to bottom, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1))',
                                borderLeft: '1px solid rgba(34, 197, 94, 0.5)',
                                borderRight: '1px solid rgba(34, 197, 94, 0.5)',
                                zIndex: 1
                            }} />

                            {/* Good Zone */}
                            <div style={{
                                position: 'absolute', left: '35%', width: '30%', height: '100%',
                                background: 'rgba(234, 179, 8, 0.05)',
                                zIndex: 0
                            }} />

                            {/* Needle */}
                            <div style={{
                                position: 'absolute',
                                left: `${position}%`,
                                height: '100%',
                                width: '4px',
                                background: '#f87171',
                                boxShadow: '0 0 15px #f87171',
                                transform: 'translateX(-50%)',
                                zIndex: 5,
                                transition: phase === 'AIMING' ? 'none' : 'left 0.1s cubic-bezier(0,0,0.2,1)'
                            }} />
                        </div>
                    </div>

                    {/* Interaction / Results Layer */}
                    <div style={{
                        minHeight: '120px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        marginTop: phase === 'RESULTS' ? '20px' : '0'
                    }}>
                        {phase === 'AIMING' && (
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <Button variant="secondary" onClick={onClose}>Abort</Button>
                                <Button
                                    variant="danger"
                                    onClick={handleDetonate}
                                    style={{
                                        minWidth: '160px',
                                        height: '56px',
                                        fontSize: '1.25rem',
                                        fontWeight: 800,
                                        letterSpacing: '1px',
                                        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                                        boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.5), 0 2px 4px -1px rgba(239, 68, 68, 0.3)'
                                    }}
                                >
                                    DETONATE
                                </Button>
                            </div>
                        )}

                        {phase === 'EXPLODING' && (
                            <div className="blooming-text" style={{
                                fontSize: '3rem',
                                fontWeight: 900,
                                color: '#ef4444',
                                textShadow: '0 0 30px rgba(239, 68, 68, 0.8)',
                                letterSpacing: '8px'
                            }}>
                                BOOM
                            </div>
                        )}

                        {phase === 'RESULTS' && (
                            <div className="animate-pop-in" style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                <div style={{
                                    fontSize: '2.5rem',
                                    fontWeight: 900,
                                    color: getQualityColor(),
                                    textShadow: `0 0 20px ${getQualityColor()}`,
                                    marginBottom: '8px'
                                }}>
                                    {throwQuality}!
                                </div>

                                {rewards && (
                                    <div style={{
                                        display: 'flex',
                                        gap: '24px',
                                        background: 'rgba(0,0,0,0.3)',
                                        padding: '16px 32px',
                                        borderRadius: '16px',
                                        border: '1px solid rgba(255,255,255,0.05)',
                                        marginBottom: '10px'
                                    }}>
                                        {Object.entries(rewards).map(([res, amount]) => (
                                            <div key={res} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <div style={{ marginBottom: '12px', marginTop: '8px', transform: 'scale(1.5)' }}>
                                                    <ItemIcon id={res} size={42} />
                                                </div>
                                                <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff' }}>x{amount}</span>
                                                <span style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>{res}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {phase === 'RESULTS' && (
                        <div className="animate-fade-in" style={{ marginTop: '32px', display: 'flex', gap: '16px' }}>
                            <Button variant="secondary" onClick={onClose} style={{ minWidth: '100px' }}>Done</Button>
                            <Button
                                variant="primary"
                                onClick={reset}
                                style={{
                                    minWidth: '160px',
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    fontWeight: 700
                                }}
                            >
                                Throw Again 🧨
                            </Button>
                        </div>
                    )}
                </div>

                {/* Explosion Shockwave Overlay (No Flash) */}
                {phase === 'EXPLODING' && (
                    <div className="shockwave-ring" style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '0',
                        height: '0',
                        borderRadius: '50%',
                        border: '4px solid rgba(239, 68, 68, 0.8)',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: '0 0 50px rgba(239, 68, 68, 0.5)',
                        pointerEvents: 'none',
                        zIndex: 5
                    }} />
                )}
            </div>

            {/* CSS & Keyframes */}
            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translate(0, 0) rotate(0); }
                    10%, 90% { transform: translate(-2px, -2px) rotate(-1deg); }
                    20%, 80% { transform: translate(2px, 2px) rotate(1deg); }
                    30%, 70% { transform: translate(-4px, 2px) rotate(0); }
                    40%, 60% { transform: translate(4px, -2px) rotate(-1deg); }
                    50% { transform: translate(0, 4px) rotate(1deg); }
                }
                .shaking {
                    animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
                }

                @keyframes popIn {
                    0% { transform: scale(0.5); opacity: 0; }
                    60% { transform: scale(1.1); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .animate-pop-in {
                    animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                    animation-delay: 0.3s;
                    opacity: 0;
                }

                @keyframes shockwave {
                    0% { width: 0; height: 0; opacity: 1; border-width: 20px; }
                    100% { width: 800px; height: 800px; opacity: 0; border-width: 0; }
                }
                .shockwave-ring {
                    animation: shockwave 0.8s ease-out forwards;
                }

                @keyframes bloomPulse {
                    0% { filter: blur(0px) brightness(1); transform: scale(1); }
                    50% { filter: blur(2px) brightness(1.5); transform: scale(1.1); }
                    100% { filter: blur(0px) brightness(1); transform: scale(1); }
                }
                .blooming-text {
                    animation: bloomPulse 0.8s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};
